import ReactDOM from 'react-dom' // Add this import
import { useRef, useEffect, useState } from 'react'
import Icon from './svg/icon'
import useClickOutside from '@/hooks/useClickOutside'
import clsx from 'clsx'

export default function Modal({
	isOpen,
	onClose,
	children,
}: {
	isOpen: boolean
	onClose: () => void
	children: React.ReactNode
}) {
	const panelRef = useRef<HTMLDivElement | null>(null)
	const [isAnimating, setIsAnimating] = useState(false)

	const handleClose = () => {
		setIsAnimating(false) // Start close animation
		setTimeout(() => {
			onClose() // Call onClose after animation duration
		}, 300) // Match this duration with your CSS transition duration
	}

	useClickOutside(panelRef, () => {
		handleClose()
	})

	useEffect(() => {
		if (isOpen) {
			setIsAnimating(true) // Start animation when modal opens
		} else {
			setIsAnimating(false) // Reset animation when modal closes
		}
	}, [isOpen])

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleClose()
			}
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [handleClose])

	if (!isOpen) return null

	return ReactDOM.createPortal(
		<dialog
			open={isOpen}
			className={clsx(
				'fixed inset-0 z-50 w-full h-full flex items-center justify-center transition-colors duration-300',
				isAnimating ? 'bg-black/50' : 'bg-black/0'
			)}
		>
			<div
				ref={panelRef}
				className={clsx(
					'relative bg-white h-fit xl:max-h-[90vh] overflow-y-auto py-12 md:pb-16 px-3 xl:px-10 mx-3 md:mx-10 w-full rounded-lg max-w-screen-xl transition-all duration-300 m-ease',
					isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
				)}
			>
				<button
					onClick={handleClose}
					className="absolute top-2.5 right-3 bg-white-frosted rounded-md p-2 hover:bg-white-frosted-shade hover:text-blue transition-colors duration-300"
				>
					<Icon icon="Close" />
				</button>
				{children}
			</div>
		</dialog>,
		document.body
	)
}
